import type { ReactNode } from 'react'
import { BarChart2, Clock, DatabaseZap, ExternalLink } from 'lucide-react'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { BUILT } from '@/components/reports/builtReports'
import { followUpUrl, type ReportDefinition } from '@/lib/reportCatalog'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { timeFrameSummary, type ReportTimeFrame } from '@/lib/reportRange'

/**
 * The report surface: title, its controls, then its content.
 *
 * The header stays to one line. The tree carries the descriptions in its
 * tooltips, and the toolbar underneath already states the period and every
 * applied dimension, so a screenshot is readable on its own without a paragraph
 * of preamble pushing the chart down the page.
 *
 * Nothing here fetches. The five child tickets replace `Awaiting` with the real
 * report per category; the two blocked ones stay as they are until a data model
 * exists to report on.
 */
export function ReportDetail({
  report,
  categoryName,
  frame,
  filters,
  onFrame,
  onFilters,
}: {
  report: ReportDefinition
  categoryName: string
  frame: ReportTimeFrame
  filters: ReportFilterSelection
  onFrame: (next: ReportTimeFrame) => void
  onFilters: (next: ReportFilterSelection) => void
}) {
  return (
    <div className='flex h-full flex-col'>
      {/* Same primary blue as the panel header beside it — the two are the
          report's title bars and should read as one surface. */}
      <header className='flex shrink-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-1 bg-primary px-4 py-3 text-primary-foreground'>
        <h3 className='truncate text-base font-semibold' title={report.description}>
          <span className='mr-1.5 font-normal tabular-nums text-primary-foreground/70'>
            {report.id}
          </span>
          {report.name}
        </h3>
        <span className='shrink-0 text-xs text-primary-foreground/80'>{timeFrameSummary(frame)}</span>
      </header>

      <ReportToolbar
        report={report}
        frame={frame}
        filters={filters}
        onFrame={onFrame}
        onFilters={onFilters}
      />

      <div className='min-h-0 flex-1 overflow-auto bg-muted/20'>
        {report.status.kind === 'blocked' ? (
          <Notice
            icon={<DatabaseZap className='h-4 w-4 shrink-0 text-amber-500' />}
            title='No data source yet'
            detail={report.status.reason}
            footer={<FollowUp status={report.status} />}
          />
        ) : BUILT[report.id] ? (
          (() => {
            const Body = BUILT[report.id]
            return <Body frame={frame} filters={filters} />
          })()
        ) : (
          <Notice
            icon={<Clock className='h-4 w-4 shrink-0 text-muted-foreground' />}
            title='Not yet available'
            detail={`Waiting on the ${categoryName} report ticket.`}
          />
        )}
      </div>
    </div>
  )
}

function FollowUp({ status }: { status: Extract<ReportDefinition['status'], { kind: 'blocked' }> }) {
  const url = followUpUrl(status.followUp)
  if (!url) {
    // Never a dead link: until the ticket is cut, this stays plain text.
    return <>Follow-up: {status.followUp.summary} (ticket not cut yet)</>
  }
  return (
    <a
      href={url}
      target='_blank'
      rel='noreferrer'
      className='inline-flex items-center gap-1 text-primary underline underline-offset-2'
    >
      {status.followUp.summary}
      <ExternalLink className='h-3 w-3' />
    </a>
  )
}

/** Nothing picked yet. */
export function NoReportSelected() {
  return (
    <Notice
      icon={<BarChart2 className='h-4 w-4 shrink-0 text-muted-foreground' />}
      title='Choose a report'
      detail='Pick one from the list.'
    />
  )
}

function Notice({
  icon,
  title,
  detail,
  footer,
}: {
  icon: ReactNode
  title: string
  detail: string
  footer?: ReactNode
}) {
  return (
    <div className='flex items-start gap-2 px-4 py-4 text-xs text-muted-foreground'>
      {icon}
      <span>
        <span className='font-medium text-foreground'>{title}.</span> {detail}
        {footer && <span className='ml-1'>{footer}</span>}
      </span>
    </div>
  )
}
